import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { CalendarDays, Clock, Hourglass, AlertTriangle } from 'lucide-react';
import { Modal, Button, FormField } from '../../components';
import leaveService from '../../services/leaveService';
import { costOfRange, costOfHours, quantityLabel } from './leaveUnits';

/**
 * Apply for leave.
 *
 * THREE shapes, one form, and which of them are offered depends on the
 * organisation's leave unit (Organisation settings → Leave policy):
 *
 *   • "a few hours off today"   → one date + start/end time, inside that
 *                                 day's shift. The rota needs the exact window.
 *   • "N hours off"             → a quantity of hours from a start date, which
 *                                 may run over more than one shift. 8 hours off
 *                                 a 4-hour shift is two of your working days.
 *   • "I'm away next week"      → a start date and an end date.
 *
 * An organisation set to 'days' gets only the last one; the other two are hidden
 * here and refused by the API.
 *
 * The COST preview is the part worth care. It is computed from the same two
 * facts the server uses — this person's own shift length and which weekdays they
 * work, both carried on the balance payload — so what the form promises and what
 * the API charges are the same number. See dashboard/src/pages/leave/leaveUnits.js
 * and api/utils/leaveBalance.js.
 */

const LEAVE_TYPES = [
  { value: 'sick', label: 'Sickness' },
  { value: 'annual', label: 'Annual Leave' },
  { value: 'other', label: 'Other' },
];

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const emptyForm = () => ({
  leave_type: 'sick',
  duration_type: 'full_day',
  start_date: todayStr(),
  end_date: todayStr(),
  start_time: '09:00',
  end_time: '13:00',
  hours: '8',
  notes: '',
});

const ApplyLeaveModal = ({ isOpen, onClose, onSubmitted, balance = null }) => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  // The shift rostered for the chosen date — part-of-a-shift leave must sit inside it.
  const [shift, setShift] = useState(null);
  const [shiftLoading, setShiftLoading] = useState(false);

  // An organisation recording leave in whole days only. The hourly modes are not
  // offered at all rather than offered and rejected.
  const daysOnly = balance?.org_unit === 'days';
  const unit = balance?.unit || 'days';
  const dayHours = balance?.day_hours || 8;
  const workingDays = balance?.working_days || [];

  // Reset on every open so a cancelled request doesn't come back half-filled.
  // Full days is the default mode and the only one always available, so it needs
  // no adjusting for a days-only organisation.
  useEffect(() => {
    if (isOpen) {
      setForm(emptyForm());
      setShift(null);
    }
  }, [isOpen]);

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const onChange = (e) => set(e.target.name, e.target.value);

  const mode = form.duration_type;
  const isWindow = mode === 'hours';
  const isHoursTotal = mode === 'hours_total';

  /**
   * Look up the shift whenever the date (or the mode) changes, and snap the time
   * pickers to it. Part-of-a-shift leave comes out of a shift, so defaulting to
   * the shift's own hours is both the common case and the only range the API
   * will accept.
   */
  useEffect(() => {
    if (!isOpen || !isWindow || !form.start_date) return undefined;

    let cancelled = false;
    setShiftLoading(true);

    leaveService.getShiftWindow(form.start_date)
      .then((res) => {
        if (cancelled) return;
        const window = res?.data || null;
        setShift(window);
        if (window?.works && window.start && window.end) {
          // Snap to the full shift. People most often take the start or the end
          // of a shift off, and dragging in from a valid range beats being
          // rejected for guessing outside it.
          setForm((f) => ({ ...f, start_time: window.start, end_time: window.end }));
        }
      })
      .catch(() => { if (!cancelled) setShift(null); })
      .finally(() => { if (!cancelled) setShiftLoading(false); });

    return () => { cancelled = true; };
  }, [isOpen, isWindow, form.start_date]);

  // Only a same-day shift can clamp the inputs. An overnight shift wraps past
  // midnight, so min/max on a time input would exclude the small hours that are
  // genuinely part of it — leave those unclamped and let the API judge.
  const clamp = shift?.works && !shift?.overnight
    ? { min: shift.start, max: shift.end }
    : {};

  /**
   * What this request costs, in the same terms the allowance is kept in.
   *
   * `null` when the form isn't complete enough to price yet — the preview then
   * simply doesn't appear rather than showing a misleading zero.
   */
  const cost = (() => {
    if (isWindow) {
      const [sh, sm] = String(form.start_time || '').split(':').map(Number);
      const [eh, em] = String(form.end_time || '').split(':').map(Number);
      if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return null;
      const mins = (eh * 60 + em) - (sh * 60 + sm);
      if (mins <= 0) return null;
      return costOfHours(mins / 60, dayHours);
    }
    if (isHoursTotal) {
      const hours = Number(form.hours);
      if (!Number.isFinite(hours) || hours <= 0) return null;
      return costOfHours(hours, dayHours);
    }
    if (!form.start_date || !form.end_date || form.start_date > form.end_date) return null;
    return costOfRange(form.start_date, form.end_date, dayHours, workingDays);
  })();

  // Only annual leave draws on the allowance, so only annual leave can overdraw
  // it. Warning rather than blocking: the API is the authority and its message
  // is better than anything guessed at here.
  const overdrawn = cost && form.leave_type === 'annual' && balance
    && cost.days > balance.remaining_days;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mirror the server's rules so the common mistakes are caught without a
    // round trip; the API still re-validates everything it is sent.
    if (isWindow) {
      if (!form.start_date) return toast.error('Pick the date you need off.');
      if (!form.start_time || !form.end_time) return toast.error('Enter a start and end time.');
      if (form.end_time <= form.start_time) return toast.error('The end time must be after the start time.');
      // Same rule the API applies, said earlier. Overnight shifts are left to
      // the server, which can do the past-midnight maths properly.
      if (shift?.hasRoster && !shift.works) {
        return toast.error("You're not scheduled to work that day — apply for full-day leave instead.");
      }
      if (shift?.works && !shift.overnight
        && (form.start_time < shift.start || form.end_time > shift.end)) {
        return toast.error(`Your shift that day is ${shift.start}–${shift.end}. Leave hours must be within your shift.`);
      }
    } else if (isHoursTotal) {
      if (!form.start_date) return toast.error('Pick the date your leave starts.');
      const hours = Number(form.hours);
      if (!Number.isFinite(hours) || hours <= 0) return toast.error('Enter how many hours you need.');
    } else {
      if (!form.start_date || !form.end_date) return toast.error('Pick a start and end date.');
      if (form.start_date > form.end_date) return toast.error('The start date cannot be after the end date.');
    }
    if (form.leave_type === 'other' && !form.notes.trim()) {
      return toast.error('Please add a note explaining the reason.');
    }

    let payload;
    if (isWindow) {
      payload = {
        leave_type: form.leave_type,
        duration_type: 'hours',
        start_date: form.start_date,
        start_time: form.start_time,
        end_time: form.end_time,
        notes: form.notes,
      };
    } else if (isHoursTotal) {
      // No end date: the API works it out from how many of this person's shifts
      // the hours consume. Sending one would only give it something to disagree
      // with.
      payload = {
        leave_type: form.leave_type,
        duration_type: 'hours_total',
        start_date: form.start_date,
        hours: Number(form.hours),
        notes: form.notes,
      };
    } else {
      payload = {
        leave_type: form.leave_type,
        duration_type: 'full_day',
        start_date: form.start_date,
        end_date: form.end_date,
        notes: form.notes,
      };
    }

    try {
      setSubmitting(true);
      const res = await leaveService.applyLeave(payload);
      if (res?.success) {
        toast.success(res.message || 'Leave request submitted');
        // Hand the fresh balance back so the caller doesn't have to re-fetch it
        // and briefly show the old figure.
        onSubmitted?.(res.balance || null);
        onClose();
      } else {
        toast.error(res?.message || 'Could not submit your request');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Could not submit your request');
    } finally {
      setSubmitting(false);
    }
  };

  const modeButton = (value, label, Icon) => {
    const active = mode === value;
    return (
      <button
        key={value}
        type="button"
        onClick={() => set('duration_type', value)}
        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-[13px] font-semibold transition border ${active
          ? 'bg-blue-600 text-white border-blue-600 shadow-[0_4px_12px_-4px_rgba(37,99,235,0.6)]'
          : 'bg-white dark:bg-[#31373f] text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#3a4048]'
          }`}
      >
        <Icon className="w-4 h-4" />
        {label}
      </button>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Apply for leave" maxWidth="max-w-[560px]">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* What's left, stated before anything is filled in — the figure that
            decides whether this request is worth making at all. */}
        {balance && (
          <div className="flex items-center justify-between px-3.5 py-3 rounded-lg bg-[#F8FAFC] dark:bg-[#31373f] border border-[#F1F5F9] dark:border-gray-700">
            <div>
              <p className="text-[11.5px] font-semibold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">
                Annual leave left ({balance.year})
              </p>
              <p className="text-[19px] font-bold text-gray-800 dark:text-gray-100 leading-tight">
                {quantityLabel(balance.remaining_days, dayHours, unit)}
              </p>
            </div>
            <div className="text-right text-[12px] text-[#64748B] dark:text-gray-400 leading-relaxed">
              <p>{quantityLabel(balance.entitlement_days, dayHours, unit)} allowance</p>
              <p>{quantityLabel(balance.used_days, dayHours, unit)} taken</p>
              {balance.pending_days > 0 && (
                <p>{quantityLabel(balance.pending_days, dayHours, unit)} awaiting approval</p>
              )}
            </div>
          </div>
        )}

        <div>
          <p className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 mb-2">
            How much time do you need?
          </p>
          <div className="flex gap-2">
            {/* Hidden entirely when the organisation records leave in whole
                days — an option the API will refuse is worse than no option. */}
            {!daysOnly && modeButton('hours', 'Part of a shift', Clock)}
            {!daysOnly && modeButton('hours_total', 'Hours', Hourglass)}
            {modeButton('full_day', 'Full day(s)', CalendarDays)}
          </div>
        </div>

        <FormField
          label="Leave type"
          name="leave_type"
          type="select"
          value={form.leave_type}
          onChange={onChange}
          options={LEAVE_TYPES}
          required
        />

        {isWindow && (
          <>
            <FormField
              label="Date"
              name="start_date"
              type="date"
              value={form.start_date}
              onChange={onChange}
              required
            />
            {/* What the times are being measured against. Without this the
                min/max on the inputs look arbitrary when they bite. */}
            {shiftLoading ? (
              <p className="text-[12.5px] text-[#94A3B8] dark:text-gray-500">Checking your shift…</p>
            ) : shift?.works ? (
              <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">
                Your shift that day is{' '}
                <span className="font-bold text-gray-700 dark:text-gray-200">
                  {shift.start}–{shift.end}
                </span>
                {shift.shiftName ? ` (${shift.shiftName})` : ''}. Leave hours must be within it.
              </p>
            ) : shift?.hasRoster ? (
              <p className="text-[12.5px] text-amber-600 dark:text-amber-400">
                You're not scheduled to work that day, so there are no hours to take off.
                Pick a day you work, or switch to Hours or Full day(s).
              </p>
            ) : null}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="From"
                name="start_time"
                type="time"
                value={form.start_time}
                onChange={onChange}
                required
                {...clamp}
              />
              <FormField
                label="To"
                name="end_time"
                type="time"
                value={form.end_time}
                onChange={onChange}
                required
                {...clamp}
              />
            </div>
          </>
        )}

        {isHoursTotal && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Starting from"
                name="start_date"
                type="date"
                value={form.start_date}
                onChange={onChange}
                required
              />
              <FormField
                label="Hours needed"
                name="hours"
                type="number"
                min="0.5"
                step="0.5"
                value={form.hours}
                onChange={onChange}
                required
              />
            </div>
            {/* The one thing that is not obvious about this mode: the hours run
                across shifts, so the end date is a consequence of the figure
                rather than something to choose. */}
            <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">
              Your shift is{' '}
              <span className="font-bold text-gray-700 dark:text-gray-200">{dayHours} hours</span>
              {balance?.shift_label ? ` (${balance.shift_label})` : ''}, so these hours are spread
              across as many of your shifts as they cover, starting from this date.
            </p>
          </>
        )}

        {!isWindow && !isHoursTotal && (
          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Start date"
              name="start_date"
              type="date"
              value={form.start_date}
              onChange={(e) => {
                const v = e.target.value;
                // Dragging the start past the end is almost always a mis-click,
                // so pull the end along rather than showing a validation error.
                setForm((f) => ({ ...f, start_date: v, end_date: f.end_date < v ? v : f.end_date }));
              }}
              required
            />
            <FormField
              label="End date"
              name="end_date"
              type="date"
              value={form.end_date}
              onChange={onChange}
              min={form.start_date}
              required
            />
          </div>
        )}

        <FormField
          label={form.leave_type === 'other' ? 'Reason (required)' : 'Notes (optional)'}
          name="notes"
          type="textarea"
          rows={3}
          value={form.notes}
          onChange={onChange}
          placeholder="Anything your manager should know"
          required={form.leave_type === 'other'}
        />

        {cost && (
          <div className="space-y-1.5">
            <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">
              You're requesting{' '}
              <span className="font-bold text-gray-700 dark:text-gray-200">
                {quantityLabel(cost.days, dayHours, unit)}
              </span>
              {/* Both figures when the two readings differ, because "3 days" off
                  a part-timer's rota is the surprising number and hiding it is
                  how a balance stops making sense. */}
              {cost.note ? ` — ${cost.note}` : ''}
              {form.leave_type === 'annual'
                ? '. This comes off your annual leave allowance.'
                : '. This does not come off your annual leave allowance.'}
            </p>
            {overdrawn && (
              <p className="flex items-start gap-1.5 text-[12.5px] font-semibold text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                That is more than the {quantityLabel(balance.remaining_days, dayHours, unit)} you
                have left, so this request will be refused. Reduce it, or ask your manager to
                raise your allowance.
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-[#F1F5F9] dark:border-gray-800">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            // Nothing to take off a day they don't work, and no point posting a
            // request the allowance cannot cover — block both here rather than
            // making the round trip to be told.
            disabled={(isWindow && shift?.hasRoster && !shift.works) || overdrawn}
          >
            {submitting ? 'Submitting…' : 'Submit request'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ApplyLeaveModal;
